import React from 'react';
import fieldConfig from "./FieldConfig";
import { FiChevronDown, FiChevronUp, FiChevronRight, FiChevronLeft } from "react-icons/fi";

export default function ColumnPanel({
    availableColumns,
    selectedAvailable,
    tempDisplayedColumns,
    displayMode,
    onAvailableChange,
    onAddColumns,
    onRemoveColumns,
    onApply,
    onCancel,
    onMoveUp,
    onMoveDown,
    onReset,
    toggle,
    viewType
}) {
    const [selectedDisplayedCols, setSelectedDisplayedCols] = React.useState([]); // now multiple
    // Add state to track reset mode
    const [showAllAvailableOnReset, setShowAllAvailableOnReset] = React.useState(false);
    const [columnSearch, setColumnSearch] = React.useState('');
    const [noMatches, setNoMatches] = React.useState(false);
    const avlSelectRef = React.useRef(null);

    const DEFAULT_VIEW_COLUMNS = [
        "QUERY_ALI_INFO",
        "SUBJECT_ALI_INFO",
        "SUBJECT_PS",
        "ALI_INFO",
        "SUBJECT_P1",
        "SUBJECT_CT",
        "SUBJECT_SEQIDNOSUMM",
        "SUBJECT_FAMINFO",
        "SUBJECT_D1",
        "SUBJECT_EM",
        "SUBJECT_PQ",
        "ORGANISM",
        "SUBJECT_GNAME",
    ];

    const DEFAULT_FAM_VIEW_COLUMNS = ["QUERY_ALI_INFO", "SUBJECT_ALI_INFO", "SUBJECT_P1","SUBJECT_FAMINFO","NBFAMMEMBERS","ASSIGNEES","SUBJECT_PS", "ALI_INFO", "SUBJECT_PB", "SUBJECT_XF","SUBJECT_PM"]
    const defaultDisplayedColumns = React.useMemo(() => {
        const allAvailable = Object.values(availableColumns).flat();
        let filtered = [];
        if(displayMode === 'family'){
         filtered = DEFAULT_FAM_VIEW_COLUMNS.filter(col => allAvailable.includes(col));
         return filtered.length > 0 ? filtered : DEFAULT_FAM_VIEW_COLUMNS;
        }
        else{
         filtered = DEFAULT_VIEW_COLUMNS.filter(col => allAvailable.includes(col));
         return filtered.length > 0 ? filtered : [];
        }  

        
    }, [availableColumns]);

    const allAvailableColumnsFlat = React.useMemo(
        () => Object.values(availableColumns).flat(),
        [availableColumns]
    );

    const tempColumns = React.useMemo(() => {
        if (showAllAvailableOnReset) {
            return allAvailableColumnsFlat;
        }
        // Always show default columns if not in custom view and no custom columns selected
        if (viewType !== "custom" && (!tempDisplayedColumns || tempDisplayedColumns.length === 0)) {
            return defaultDisplayedColumns;
        }
        if (viewType === "default") return defaultDisplayedColumns;
        if (!tempDisplayedColumns || tempDisplayedColumns.length === 0) return defaultDisplayedColumns;
        return tempDisplayedColumns;
    }, [
        showAllAvailableOnReset,
        allAvailableColumnsFlat,
        viewType,
        tempDisplayedColumns,
        defaultDisplayedColumns
    ]);

    const handleReset = () => {
        if (onReset) onReset();
        setSelectedDisplayedCols([]);
        setShowAllAvailableOnReset(true);
    };

    // When user applies or cancels, stop showing all available columns
    React.useEffect(() => {
        if (viewType === "custom" && !showAllAvailableOnReset) return;
        setShowAllAvailableOnReset(false);
    }, [onApply, onCancel, viewType]);
    
    const moveUp = idx => {
        if (idx > 0) {
            const newColumns = [...tempColumns];
            [newColumns[idx - 1], newColumns[idx]] = [newColumns[idx], newColumns[idx - 1]];
            if (onMoveUp) onMoveUp(idx, newColumns);
            setSelectedDisplayedCols([newColumns[idx - 1]]);
        }
    };

    const moveDown = idx => {
        if (idx < tempColumns.length - 1) {
            const newColumns = [...tempColumns];
            [newColumns[idx + 1], newColumns[idx]] = [newColumns[idx], newColumns[idx + 1]];
            if (onMoveDown) onMoveDown(idx, newColumns);
            setSelectedDisplayedCols([newColumns[idx + 1]]);
        }
    };

    const displayedSet = new Set(tempColumns);

    const filteredAvailableColumns = Object.fromEntries(
        Object.entries(availableColumns).map(([group, cols]) => [
            group,
            cols.filter(col => !displayedSet.has(col))
        ])
    );

    React.useEffect(() => {
        if (!avlSelectRef.current) return;
        const search = columnSearch.toLowerCase().trim();

        if (!search) {
            avlSelectRef.current.querySelectorAll('option').forEach(opt => opt.style.display = '');
            avlSelectRef.current.querySelectorAll('optgroup').forEach(grp => grp.style.display = '');
            setNoMatches(false);
            return;
        }

        let totalVisible = 0;
        avlSelectRef.current.querySelectorAll('optgroup').forEach(grp => {
            const options = grp.querySelectorAll('option');
            let hasVisible = false;
            options.forEach(opt => {
                const matches = opt.textContent.toLowerCase().includes(search);
                opt.style.display = matches ? '' : 'none';
                if (matches) { hasVisible = true; totalVisible++; }
            });
            grp.style.display = hasVisible ? '' : 'none';
        });
        setNoMatches(totalVisible === 0);
    }, [columnSearch, filteredAvailableColumns]);

    const handleMoveUp = () => {
        if (selectedDisplayedCols.length !== 1) return;
        const idx = tempColumns.indexOf(selectedDisplayedCols[0]);
        moveUp(idx);
    };

    const handleMoveDown = () => {
        if (selectedDisplayedCols.length !== 1) return;
        const idx = tempColumns.indexOf(selectedDisplayedCols[0]);
        moveDown(idx);
    };

    const handleRemoveColumns = () => {
        if (selectedDisplayedCols.length === 0) return;
        onRemoveColumns(selectedDisplayedCols); // now passes an array
        setSelectedDisplayedCols([]);
    };

    const getColumnDisplayName = (col) => {
        const specialNames = {
            'SUBJECT_RB': 'CAS RN® Entry Date',
            'SUBJECT_RC': 'CAS RN® Last Update Date',
            'SUBJECT_RG': 'CAS Registry Number®',
            'SUBJECT_ZA': 'Title',
            'SUBJECT_ZB': 'Commercial Impact',
            'SUBJECT_ZC': 'Summary',
            'SUBJECT_ZD': 'Claim Analysis',
            'SUBJECT_ZE': 'SeqID Summary',
        };

        if (specialNames[col]) {
            return specialNames[col];
        }

        if (typeof window !== 'undefined' && window.fieldConfig && window.fieldConfig.getFieldInfo) {
            const info = window.fieldConfig.getFieldInfo(col);
            return (info && info.title) ? info.title : col;
        }
        if (typeof fieldConfig !== 'undefined' && fieldConfig && fieldConfig.getFieldInfo) {
            const info = fieldConfig.getFieldInfo(col);
            return (info && info.title) ? info.title : col;
        }
        return col.replace(/_/g, ' ').replace(/\b([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim();
    };

    return (
        <div style={{ padding: '24px', minHeight: '400px' }}>
            {viewType !== "default" && (
                <>
                    <h4 className='really-large-font field-optgroup' style={{ textAlign: 'center' }}>Custom View Settings</h4>
                    <div className='field-optgroup'>Create your personalized view.</div>
                </>
            )}
            {viewType !== "custom" && (
                <>
                    <h4 className='really-large-font field-optgroup' style={{ textAlign: 'center' }}>Default View Columns</h4>
                    <div className='field-optgroup'></div>
                </>
            )}
            <div style={{ display: 'flex', gap: '32px', alignItems: 'stretch' }}>
                <div style={{ flex: 1 }}>
                    <div className='large-font field-container-label'>Available Columns</div>
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                    <input
                        type="text"
                        placeholder="Search columns..."
                        value={columnSearch}
                        onChange={(e) => setColumnSearch(e.target.value)}
                        style={{
                            display: 'block',
                            width: '100%',
                            padding: '6px 10px',
                            marginBottom: '4px',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            fontSize: '14px',
                            boxSizing: 'border-box',
                            backgroundColor: toggle ? '#fff' : '#292929',
                            color: toggle ? '#222' : '#fff'
                        }}
                    />
                    <select
                        multiple
                        ref={avlSelectRef}
                        className="field-container"
                        style={{ background: toggle ? '#fff' : '#292929', color: toggle ? '#222' : '#fff', boxSizing: 'border-box' }}
                        value={selectedAvailable}
                        onChange={onAvailableChange}
                    >
                        {Object.entries(filteredAvailableColumns).map(([group, cols]) =>
                            cols.length > 0 ? (
                                <optgroup key={group} label={group} className="field-optgroup-new">
                                    {cols.map(col => (
                                        <option className="field-option" key={col} value={col}>{getColumnDisplayName(col)}</option>
                                    ))}
                                </optgroup>
                            ) : null
                        )}
                    </select>
                    {noMatches && (
                        <div style={{
                            position: 'absolute',
                            left: 0,
                            right: 0,
                            bottom: 0,
                            top: '40px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            pointerEvents: 'none',
                            fontSize: '14px',
                            color: toggle ? '#888' : '#aaa',
                            fontWeight: 500
                        }}>
                            "{columnSearch}" Not Found
                        </div>
                    )}
                    </div>
                </div>

                {viewType !== "default" && (
                    <>
                        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 8, minWidth: 48 }}>
                            <div className='field-controls'>
                                <div>
                                    <FiChevronRight onClick={onAddColumns} disabled={!selectedAvailable || selectedAvailable.length === 0} title="Add to Displayed" />
                                </div>
                                <div>
                                    <FiChevronLeft onClick={handleRemoveColumns} disabled={selectedDisplayedCols.length === 0 || tempColumns.length === 0} title="Remove from Displayed" />
                                </div>
                                <div>
                                    <FiChevronUp onClick={handleMoveUp} disabled={selectedDisplayedCols.length !== 1 || tempColumns.indexOf(selectedDisplayedCols[0]) === 0} title="Move Up" />
                                </div>
                                <div>
                                    <FiChevronDown onClick={handleMoveDown} disabled={selectedDisplayedCols.length !== 1 || tempColumns.indexOf(selectedDisplayedCols[0]) === tempColumns.length - 1} title="Move Down" />
                                </div>
                            </div>
                        </div>

                        <div style={{ flex: 1 }}>
                            <div className="large-font field-container-label">Displayed Columns</div>
                            <select
                                multiple
                                className="field-container"
                                size={Math.max(5, tempColumns.length || 1)}
                                onChange={e => {
                                    const selected = Array.from(e.target.selectedOptions, opt => opt.value);
                                    setSelectedDisplayedCols(selected);
                                }}
                                style={{
                                    width: '100%',
                                    minHeight: '120px',
                                    background: toggle ? '#fff' : '#292929',
                                    color: toggle ? '#222' : '#fff',
                                    borderRadius: '4px',
                                    border: '1px solid #ccc',
                                    padding: '8px'
                                }}
                            >
                                {tempColumns.map((col, index) => (
                                    <option
                                        key={`${col}-${index}`}
                                        className="field-option"
                                        value={col}
                                        selected={selectedDisplayedCols.includes(col)}
                                        style={{
                                            background: selectedDisplayedCols.includes(col) ? (toggle ? '#e6f0fa' : '#444') : 'transparent',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        {getColumnDisplayName(col)}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </>
                )}
            </div>

            {viewType !== "default" && (
                <div style={{ marginTop: '24px', textAlign: 'right' }}>
                    <button onClick={handleReset} className="pull-left normal-button gqblue">Reset</button>
                    <button className="pull-right normal-button gqblue" onClick={onApply}>Apply</button>
                    <button className="pull-right normal-button grey" onClick={onCancel}>Cancel</button>
                </div>
            )}
        </div>
    );
}